import {TTL_USER_INFORMATION} from "@assets/js/imaios-connection-management/config.constant";

/**
 * @typedef Data
 * @property [expire] {number} a timestamp at which the data should be considered expired
 * @extends {any}
 */

class TempStorageService {
	isAvailable = false;
	static store = localStorage;

	constructor() {
		this.isAvailable = TempStorageService.checkIsAvailable();
	}

	// https://stackoverflow.com/questions/16427636/check-if-localstorage-is-available/16427747#16427747
	static checkIsAvailable() {
		const test = 'test';
		try {
			TempStorageService.store.setItem(test, test);
			TempStorageService.store.removeItem(test);
			return true;
		} catch (e) {
			return false;
		}
	}

	checkAllExpire(regex = null) {
		if (this.isAvailable) {
			const keys = Object.keys(TempStorageService.store);
			for (let i = 0; i < keys.length; i++) {
				if (regex !== null) {
					if (regex.test(keys[i])) {
						this.checkExpire(keys[i]);
					}
				} else {
					this.checkExpire(keys[i]);
				}
			}
		}
	}

	/**
	 * @private
	 * @param data {Data}
	 * @return {boolean}
	 */
	isExpired(data) {
		return data !== null && typeof data.expire !== 'undefined' && data.expire < Date.now();
	}

	checkExpire(name) {
		if (this.isAvailable) {
			const data = this.get(name);

			if (this.isExpired(data)) {
				this.remove(name);
			}
		}
	}

	/**
	 *
	 * @param name {string} the name of the data to store
	 * @param data {Data | any} the data to store
	 * @param [expire] how long to store the data for. If not provided the value of the `expire` property of the `data`
	 * parameter will be used, if this isn't defined either a default value of 300000ms (5 minutes) will be used.
	 */
	add(name, data, expire = null) {
		if (this.isAvailable) {
			data.expire = expire;
			if (expire === null) {
				data.expire = Date.now() + TTL_USER_INFORMATION; // Add expire
			}
			const oldValue = this.get(name);

			if (oldValue !== null && typeof oldValue === 'object') {
				data = { ...oldValue, ...data };
			}

			TempStorageService.store.setItem(name, JSON.stringify(data));
		}
	}

	/**
	 *
	 * @param name { string } the name of the data to get
	 * @param [checkExpiration = false] should the expiration be checked before returning, defaults to `false` to preserve
	 * previous behavior
	 * @return {any|null}
	 */
	get(name, checkExpiration = false) {
		if (this.isAvailable) {
			try {

				const unparsedJSON = TempStorageService.store.getItem(name);
				const json = JSON.parse(unparsedJSON);

				if (checkExpiration && this.isExpired(json)) {
					this.remove(name);
					return null;
				}

				return json ?? null;
			} catch (e) {
				return null;
			}
		}
		return null;
	}

	remove(name, key = null) {
		if (this.isAvailable) {
			if (key !== null) {
				const data = this.get(name);

				if (typeof data === 'object' && data !== null) {
					delete data[key];

					if (Object.keys(data).length === 0) {
						TempStorageService.store.removeItem(name);
					} else {
						TempStorageService.store.setItem(name, JSON.stringify(data));
					}
				} else {
					TempStorageService.store.removeItem(name);
				}
			} else {
				TempStorageService.store.removeItem(name);
			}
		}
	}

	has(name) {
		if (this.isAvailable) {
			return this.get(name) !== null;
		}
		return false;
	}
}

const storageService = new TempStorageService();
export default storageService;
